import { createMemo, createSignal } from "solid-js";
import type { Coordinates } from "~/solidJs/shared/helpers/types/ISizeble";
import { Menu } from "~/solidJs/shared/ui/molecules/Menu";
import type { EnumOption } from "./model";

// Interface for unified state
interface EnumContextMenuState {
	isOpen: boolean;
	position: Coordinates;
	socketName: string | null;
	nodeUuid: string | null;
	options: EnumOption[];
	setValue: ((value: string) => void) | null;
	selectedValue: string | null;
	onClose: (() => void) | null;
	searchQuery: string;
	searchedOptions: EnumOption[];
}

// Global signal for EnumContextMenu state
const [globalState, setGlobalState] = createSignal<EnumContextMenuState>({
	isOpen: false,
	position: { left: 0, top: 0 },
	socketName: null,
	nodeUuid: null,
	options: [],
	setValue: null,
	selectedValue: null,
	onClose: null,
	searchQuery: "",
	searchedOptions: []
});

// Global function to open EnumContextMenu
export const openEnumContextMenu = (
	socketName: string,
	nodeUuid: string,
	position: Coordinates,
	options: EnumOption[],
	setValue: (value: string) => void,
	selectedValue: string | null = null,
	onClose: (() => void) | null = null
) => {
	setGlobalState({
		isOpen: true,
		position,
		socketName,
		nodeUuid,
		options,
		setValue,
		selectedValue,
		onClose,
		searchQuery: "",
		searchedOptions: []
	});
};

// Global function to close EnumContextMenu
export const closeEnumContextMenu = () => {
	const currentState = globalState();
	if (currentState.onClose) {
		currentState.onClose();
	}
	setGlobalState({
		isOpen: false,
		position: { left: 0, top: 0 },
		socketName: null,
		nodeUuid: null,
		options: [],
		setValue: null,
		selectedValue: null,
		onClose: null,
		searchQuery: "",
		searchedOptions: []
	});
};

const createEnumMenuItem = (option: EnumOption, closeMenu: () => void): Menu.ClosedMenuItem => {
	return {
		id: `enum-option-${option.value}`,
		type: Menu.ClosedMenuItemType.Item,
		label: option.name,
		getIsDisabled: () => !!option.disabled,
		onSelect(_menuItem) {
			const currentState = globalState();
			if (currentState.setValue) {
				currentState.setValue(option.value);
			}
			closeMenu();
		}
	};
};

// Function to filter options based on search query
const filterOptions = (options: EnumOption[], searchQuery: string): EnumOption[] => {
	if (!searchQuery.trim()) {
		return [];
	}

	const lowercasedQuery = searchQuery.toLowerCase().trim();
	return options.filter(
		(option) =>
			option.name.toLowerCase().includes(lowercasedQuery) ||
			option.value.toLowerCase().includes(lowercasedQuery)
	);
};

export const useEnumContextMenu = () => {
	const actions = createMemo((): Menu.ClosedMenuItem[] => {
		const state = globalState();
		const optionsToShow = state.searchQuery.trim() ? state.searchedOptions : state.options;
		return optionsToShow.map((option) => createEnumMenuItem(option, closeEnumContextMenu));
	});

	const onSearchInput = (value: string) => {
		const currentState = globalState();
		const filteredOptions = filterOptions(currentState.options, value);

		setGlobalState({
			...currentState,
			searchQuery: value,
			searchedOptions: filteredOptions
		});
	};

	return {
		isOpen: () => globalState().isOpen,
		setIsOpen: (value: boolean) => {
			if (!value) {
				closeEnumContextMenu();
			} else {
				const currentState = globalState();
				setGlobalState({
					...currentState,
					isOpen: true,
					searchQuery: "",
					searchedOptions: []
				});
			}
		},
		position: () => globalState().position,
		socketName: () => globalState().socketName,
		nodeUuid: () => globalState().nodeUuid,
		searchQuery: () => globalState().searchQuery,
		onSearchInput,
		actions
	};
};
